import React, { useState } from "react";
import {
  FaUserShield,
  FaClock,
  FaStar,
  FaRocket,
  FaHeadset,
  FaCogs,
} from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import { NavLink } from "react-router-dom";

const features = [
  {
    icon: <FaUserShield />,
    title: "Trust & Security",
    desc: "We prioritize your data safety and privacy with top-level security protocols.",
    color: "#4E56F6",
  },
  {
    icon: <FaClock />,
    title: "Timely Delivery",
    desc: "Our agile process ensures your project is delivered on schedule, every time.",
    color: "#FF6B6B",
  },
  {
    icon: <FaStar />,
    title: "Quality Assurance",
    desc: "We deliver high-quality solutions that exceed expectations and standards.",
    color: "#FFB347",
  },
  {
    icon: <FaRocket />,
    title: "Innovative Solutions",
    desc: "We bring creativity and technology together to create cutting-edge solutions.",
    color: "#20C997",
  },
  {
    icon: <FaHeadset />,
    title: "24/7 Support",
    desc: "Our support team is available around the clock for any queries or help.",
    color: "#9775FA",
  },
  {
    icon: <FaCogs />,
    title: "Custom Solutions",
    desc: "We tailor services to your unique business needs and objectives.",
    color: "#3DB2FF",
  },
];

const WhyChooseUs = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section className="py-5" style={{ background: "#F8F9FD",marginBottom:"-110px" }}>
      {/* Background decorative elements */}
      <div
        style={{
          position: "absolute",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(14, 14, 139, 0.1) 0%, rgba(255, 255, 255, 0) 70%)",
          top: "10%",
          left: "5%",
          borderRadius: "50%",
          zIndex: 0,
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "200px",
          height: "200px",
          background:
            "radial-gradient(circle, rgba(255, 107, 107, 0.1) 0%, rgba(255, 255, 255, 0) 70%)",
          bottom: "10%",
          right: "10%",
          borderRadius: "50%",
          zIndex: 0,
        }}
      />

      <div className="container position-relative" style={{ zIndex: 1 }}>
        {/* Header section with animated reveal */}
        <div
          className="text-center mb-5"
          style={{
            opacity: 1,
            transform: "translateY(0)",
            transition: "opacity 0.8s ease, transform 0.8s ease",
            animation: "fadeInUp 1s ease",
          }}
        >
          <div className="d-inline-block position-relative mb-2">
            <span
              className="badge rounded-pill"
              style={{
                background:
                  "linear-gradient(90deg,rgb(11, 19, 175) 0%,rgb(8, 101, 240) 100%)",
                color: "white",
                padding: "0.5rem 1.5rem",
                fontSize: "0.85rem",
                fontWeight: "500",
                letterSpacing: "1px",
              }}
            >
              WHY CLIENTS CHOOSE US
            </span>
          </div>
          <h2
            className="mb-3 display-5 fw-bold"
            style={{
              background: "linear-gradient(90deg, #222 0%, #555 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Delivering Excellence in Every Project
          </h2>
          <p
            className="text-secondary mx-auto"
            style={{
              maxWidth: "700px",
              fontSize: "1.1rem",
              lineHeight: "1.6",
            }}
          >
            Our core values drive us to exceed expectations and build lasting
            partnerships with our clients.
          </p>
        </div>

        {/* Features grid with staggered animation */}
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
          {features.map((item, index) => (
            <div
              className="col"
              key={index}
              style={{
                opacity: 1,
                transform: "translateY(0)",
                transition: "opacity 0.6s ease, transform 0.6s ease",
                animation: `fadeInUp ${0.3 + index * 0.1}s ease`,
              }}
            >
              <div
                className="h-100 p-4 rounded-4 position-relative overflow-hidden"
                style={{
                  background: "white",
                  boxShadow:
                    hoveredIndex === index
                      ? `0 20px 30px rgba(0, 0, 0, 0.08), 0 0 0 2px ${item.color}20`
                      : "0 10px 20px rgba(0, 0, 0, 0.03)",
                  transform:
                    hoveredIndex === index
                      ? "translateY(-5px)"
                      : "translateY(0)",
                  transition: "all 0.4s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Background gradient on hover */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${item.color}05 0%, ${item.color}15 100%)`,
                    opacity: hoveredIndex === index ? 1 : 0,
                    transition: "opacity 0.4s ease",
                    zIndex: 0,
                  }}
                />

                {/* Content */}
                <div className="position-relative" style={{ zIndex: 1 }}>
                  <div
                    className="d-inline-flex align-items-center justify-content-center mb-4 rounded-3"
                    style={{
                      width: "70px",
                      height: "70px",
                      background:
                        hoveredIndex === index ? item.color : `${item.color}15`,
                      fontSize: hoveredIndex === index ? "1.8rem" : "1.6rem",
                      color: hoveredIndex === index ? "white" : item.color,
                      transition: "all 0.4s ease",
                      transform:
                        hoveredIndex === index ? "scale(1.1)" : "scale(1)",
                    }}
                  >
                    {item.icon}
                  </div>

                  <h4
                    className="fw-bold mb-3"
                    style={{
                      fontSize: "1.3rem",
                      color: hoveredIndex === index ? item.color : "#333",
                      transition: "color 0.4s ease",
                    }}
                  >
                    {item.title}
                  </h4>

                  <p
                    className="mb-0"
                    style={{
                      color: "#6c757d",
                      lineHeight: "1.6",
                      fontSize: "0.95rem",
                    }}
                  >
                    {item.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to action button */}
        <div
          className="text-center mt-5"
          style={{
            opacity: 1,
            animation: "fadeInUp 1.2s ease",
          }}
        >
          <button
            className="btn px-4 py-2 rounded-pill"
            style={{
              background:
                "linear-gradient(90deg,rgb(11, 19, 175) 0%,rgb(8, 101, 240) 100%)",
              color: "white",
              fontWeight: "500",
              border: "none",
              boxShadow: "0 10px 20px rgba(78, 86, 246, 0.2)",
              transition: "all 0.3s ease",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow =
                "0 15px 25px rgba(78, 86, 246, 0.3)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 10px 20px rgba(78, 86, 246, 0.2)";
            }}
          >
            <NavLink to="/contact" className="text-decoration-none text-white">
              Schedule a Consultation
            </NavLink>
          </button>
        </div>
      </div>

      {/* CSS Animation Keyframes */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default WhyChooseUs;
